<template>
  <v-table class="deposits-table">
    <thead v-if="headers">
      <tr>
        <th>Name</th>
        <th>Accession Number</th>
        <th>Identifier</th>
        <th>ArchivesSpace Accession ID</th>
        <th>ArchivesSpace Archival Object ID</th>
        <th>Collecting Area</th>
        <th>Created date</th>
        <th>Status</th>
        <th>Actions</th>
        <th v-if="enableEdit && isAdmin">
          Edit
        </th>
        <th v-if="isAdmin">
          Submitted
        </th>
        <th>View this deposit</th>
      </tr>
    </thead>

    <tbody>
      <template
        v-for="(deposit, idx) in deposits"
        :key="deposit.id"
      >
        <!-- Group header row (appears at first row and whenever status changes) -->
        <tr
          v-if="groupByStatus && (idx === 0 || deposits[idx - 1].status !== deposit.status)"
          class="section-header-row"
        >
          <td :colspan="enableEdit ? 9 : 8">
            <strong>{{ deposit.status === 'open' ? 'Open deposits' : 'Closed deposits' }}</strong>
            <span
              v-if="deposit.status !== 'open'"
              class="muted"
            >
              These deposits have been closed and submitted for ingest, so no new files can be added to them.
            </span>
          </td>
        </tr>

        <tr>
          <td class="truncate">
            {{ deposit.name || "—" }}
          </td>
          <td class="truncate">
            <!-- Normal case: accessionNumber present -->
            <template v-if="deposit.accessionNumber">
              {{ deposit.accessionNumber }}
            </template>

            <!-- Fallback preview: show externalIdentifier in red with tooltip -->
            <template v-else-if="deposit.externalIdentifier && deposit.externalIdentifier.trim() !== ''">
              <span
                class="fallback-inline"
                role="text"
              >
                <span class="fallback-red fallback-number">
                  {{ deposit.externalIdentifier }}
                </span>

                <v-tooltip
                  text="Please open and re-save this Deposit before closing to correctly store this value."
                  location="top"
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-icon
                      v-bind="props"
                      size="16"
                      class="fallback-icon"
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                </v-tooltip>
              </span>
            </template>
            <template v-else>
              —
            </template>
          </td>
          <td class="truncate">
            {{ deposit.identifier || "—" }}
          </td>
          <td class="truncate">
            {{ deposit.accessionId || "—" }}
          </td>
          <td class="truncate">
            {{ deposit.archivesSpaceArchivalObjectRecordId || "—" }}
          </td>
          <td class="truncate">
            {{ deposit.collectingArea || "-" }}
          </td>
          <td class="truncate">
            {{ formatDate(deposit.createdDate) || "—" }}
          </td>
          <td
            v-if="deposit.status === 'open'"
            class="truncate"
          >
            <span class="open-text">Open</span>
          </td>
          <td
            v-else-if="deposit.status === 'closed'"
            class="truncate"
          >
            <span class="closed-text">Closed</span>
          </td>

          <td class="long-button">
            <!-- Open: Close & Submit -->
            <v-btn
              v-if="deposit.status === 'open'"
              block
              :disabled="!isAdmin || isCloseBlocked(deposit)"
              :title="isCloseBlocked(deposit)
                ? 'Please open and re-save this Deposit before closing to correctly store this value.'
                : 'Close & Submit'"
              @click="askClose(deposit)"
            >
              <span class="btn-text">Close &amp; Submit</span>
            </v-btn>

            <!-- Closed, NOT yet submitted: can reopen -->
            <v-btn
              v-else-if="deposit.status === 'closed' && !deposit.lastSubmittedForIngest"
              block
              :disabled="!isAdmin"
              @click="toggleDeposit(deposit.id)"
            >
              Reopen
            </v-btn>

            <!-- Closed AND has lastSubmittedForIngest: locked => RESUBMIT -->
            <v-btn
              v-else-if="deposit.status === 'closed' && deposit.lastSubmittedForIngest"
              block
              :disabled="!isAdmin"
              @click="askResubmit(deposit)"
            >
              Resubmit
            </v-btn>
          </td>

          <td v-if="enableEdit">
            <v-btn
              icon
              variant="text"
              :title="`Edit ${deposit.name}`"
              :disabled="disableEditWhenClosed && deposit.status !== 'open'"
              @click="openEdit(deposit)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </td>

          <td
            v-if="deposit.lastSubmittedForIngest != null && isAdmin"
            class="truncate"
          >
            {{ formatDate(deposit.lastSubmittedForIngest) }}
          </td>
          <td
            v-else-if="deposit.lastSubmittedForIngest == null && isAdmin"
            class="truncate"
          >
            —
          </td>

          <td class="view-cell">
            <v-btn
              icon
              variant="text"
              color="secondary"
              :title="`Access the deposit area`"
              :to="{
                name: 'DepositFileHome',
                params: { deposit_id: deposit.id },
                query: {
                  depositor: deposit.depositor?.['custom:identityid'],
                  deposit_name: deposit.name,
                  ...(adminUserEmail ? {
                    admin_user: adminUserEmail,
                    ...(adminUserName ? { admin_user_name: adminUserName } : {}),
                    ...(adminSearch ? { admin_search: adminSearch } : {}),
                    ...(adminPage > 1 ? { admin_page: adminPage } : {}),
                  } : {})
                }
              }"
            >
              <v-icon size="32">
                mdi-arrow-right-bold-circle
              </v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </tbody>
  </v-table>

  <!-- Edit dialog -->
  <v-dialog
    v-model="editDialog"
    max-width="960"
    @update:model-value="onEditDialogToggle"
  >
    <v-card>
      <v-card-title class="text-h6">
        Edit deposit details
      </v-card-title>

      <v-card-text>
        <v-form
          ref="editForm"
          v-model="editFormValid"
          lazy-validation
          class="form-narrow"
        >
          <!-- Name -->
          <div class="field w-80">
            <div class="label-row">
              <span
                id="lbl-name"
                class="form-label"
              >Name</span>
              <v-tooltip
                text="E.g. 'CUL Records'. This will be the 'Title' field of the package when this Deposit is ingested into the repository."
                content-class="tip-wrap-100"
              >
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    class="info-icon"
                    icon
                    size="x-small"
                    variant="text"
                    :ripple="false"
                    :aria-label="'Help: Name'"
                  >
                    <v-icon>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
            </div>

            <v-text-field
              id="name"
              v-model="editableDeposit.name"
              :aria-labelledby="'lbl-name'"
              placeholder="Enter name"
              :rules="nameRules"
              :counter="250"
              clearable
              variant="outlined"
            />
          </div>

          <!-- Collecting Area -->
          <div class="field w-80">
            <div class="label-row">
              <span
                id="lbl-colarea"
                class="form-label"
              >Collecting Area</span>
              <v-tooltip
                text="The Collecting Area this Deposit will be assigned to when ingested into the repository."
                content-class="tip-wrap-100"
              >
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    class="info-icon"
                    icon
                    size="x-small"
                    variant="text"
                    :ripple="false"
                    :aria-label="'Help: Collecting Area'"
                  >
                    <v-icon>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
            </div>

            <v-select
              id="collectingArea"
              v-model="editableDeposit.collectingArea"
              :aria-labelledby="'lbl-colarea'"
              :items="collectingAreaOptions"
              placeholder="Select collecting area"
              :rules="collectingAreaRules"
              clearable
              variant="outlined"
            />
          </div>

          <!-- Pair 1: Accession Number OR Identifier -->
          <div class="pair-row">
            <div class="field">
              <div class="label-row">
                <span
                  id="lbl-accessionNumber"
                  class="form-label"
                >Accession Number</span>
                <v-tooltip
                  text="E.g. 'GBR/0265/2025/032'. This will be the 'Accession Number' field of the package when this Deposit is ingested into the repository."
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="info-icon"
                      icon
                      size="x-small"
                      variant="text"
                      :ripple="false"
                      :aria-label="'Help: Accession Number'"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>

              <v-text-field
                id="accessionNumber"
                v-model="editableDeposit.accessionNumber"
                :aria-labelledby="'lbl-accessionNumber'"
                class="pair-left"
                placeholder="Enter accession number"
                :rules="externalIdRules"
                :counter="200"
                clearable
                variant="outlined"
              />
            </div>

            <div
              class="or-chip"
              aria-hidden="true"
            >
              OR
            </div>

            <div class="field">
              <div class="label-row">
                <span
                  id="lbl-identifier"
                  class="form-label"
                >Identifier</span>
                <v-tooltip
                  text="E.g. 'GBR/0012/MS SPR/107'. This will be the 'Identifier' field of the package when this Deposit is ingested into the repository."
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="info-icon"
                      icon
                      size="x-small"
                      variant="text"
                      :ripple="false"
                      :aria-label="'Help: Identifier'"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>

              <v-text-field
                id="identifier"
                v-model="editableDeposit.identifier"
                :aria-labelledby="'lbl-identifier'"
                class="pair-right"
                placeholder="Enter identifier"
                :rules="identifierRules"
                :counter="200"
                clearable
                variant="outlined"
              />
            </div>
          </div>

          <!-- Pair 2: ArchivesSpace Accession ID OR ArchivesSpace Archival Object ID -->
          <div class="pair-row">
            <div class="field">
              <div class="label-row">
                <span
                  id="lbl-accessionId"
                  class="form-label"
                >ArchivesSpace Accession ID</span>
                <v-tooltip
                  text="E.g. '27510'. You can find this number at the end of the URL to the accession record in ArchivesSpace, e.g. https://arcspace-staff.lib.cam.ac.uk/accessions/27510. This will be the 'ID of ArchivesSpace Accession Record' field of the package when this Deposit is ingested into the repository."
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="info-icon"
                      icon
                      size="x-small"
                      variant="text"
                      :ripple="false"
                      :aria-label="'Help: ArchivesSpace Accession ID'"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>

              <v-text-field
                id="accessionId"
                v-model="editableDeposit.accessionId"
                :aria-labelledby="'lbl-accessionId'"
                class="pair-left"
                placeholder="Digits only"
                :rules="accessionRules"
                :counter="200"
                clearable
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                variant="outlined"
              />
            </div>

            <div
              class="or-chip"
              aria-hidden="true"
            >
              OR
            </div>

            <div class="field">
              <div class="label-row">
                <span
                  id="lbl-aaoid"
                  class="form-label"
                >ArchivesSpace Archival Object ID</span>
                <v-tooltip
                  text="E.g. '1728103'. You can find this number at the end of the URL to the record in ArchivesSpace, e.g. https://arcspace-staff.lib.cam.ac.uk/resources/13400#tree::archival_object_1728103. This will be the 'ID of ArchivesSpace Archival Object Record' field of the package when this Deposit is ingested into the repository."
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="info-icon"
                      icon
                      size="x-small"
                      variant="text"
                      :ripple="false"
                      :aria-label="'Help: ArchivesSpace Archival Object ID'"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>

              <v-text-field
                id="archivesSpaceArchivalObjectRecordId"
                v-model="editableDeposit.archivesSpaceArchivalObjectRecordId"
                :aria-labelledby="'lbl-aaoid'"
                class="pair-right"
                placeholder="Enter Archival Object ID"
                :rules="aaoidRules"
                :counter="200"
                clearable
                variant="outlined"
              />
            </div>
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn
          variant="text"
          @click="editDialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="secondary"
          :loading="saving"
          :disabled="!canSave"
          @click="saveEdit"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog
    v-model="confirmCloseOpen"
    persistent
    max-width="660"
  >
    <v-card class="confirmCard">
      <v-card-title class="text-h6">
        Are you sure?
      </v-card-title>

      <v-card-text>
        <p>
          Closing <strong>{{ confirmTarget?.name || 'this deposit' }}</strong> will prevent the depositor from adding further files
          and immediately submit the deposit for ingest into the DPS.
        </p>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn
          variant="text"
          :disabled="confirmBusy"
          @click="cancelClose"
        >
          Cancel
        </v-btn>
        <v-btn
          color="secondary"
          :loading="confirmBusy"
          @click="confirmClose"
        >
          Close & Submit
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog
    v-model="confirmResubmitOpen"
    persistent
    max-width="660"
  >
    <v-card class="confirmCard">
      <v-card-title class="text-h6">
        Are you sure?
      </v-card-title>
      <v-card-text>
        <p class="warn-line">
          Resubmitting this deposit for ingest will create a duplicate package in the DPS
          if it has already been ingested before.
        </p>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn
          variant="text"
          :disabled="confirmBusy"
          @click="cancelClose"
        >
          Cancel
        </v-btn>
        <v-btn
          color="secondary"
          :loading="confirmBusy"
          @click="confirmResubmit"
        >
          Resubmit anyway
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import config from "@/config";
import { Auth } from "@/utils/amplify-compat";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { formatDate } from "@/utils/formatDate";
import { ensureAdmin, isAdminFromToken } from '@/utils/adminTools'

export default {
  name: "DepositTable",
  props: {
    deposits: { type: Array, default: () => [] },
    headers: Boolean,
    enableEdit: { type: Boolean, default: true },
    disableEditWhenClosed: { type: Boolean, default: true },
    groupByStatus: { type: Boolean, default: false },
    adminUserEmail: { type: String, default: '' },
    adminUserName: { type: String, default: '' },
    adminSearch: { type: String, default: '' },
    adminPage: { type: Number, default: 1 },
  },
  emits: ['refresh'],
  data() {
    return {
      // editing state
      allowSaving: false,
      confirmCloseOpen: false,
      confirmResubmitOpen: false,
      confirmTarget: null,
      confirmBusy: false,
      editDialog: false,
      collectingAreaOptions: [
        'Cambridge University Digital Library',
        'Cultural Heritage Imaging Laboratory',
        'Department of Archives and Modern Manuscripts',
        'Digital Preservation',
        'Map Department',
        'Music Department',
        'Open Research Systems',
        'World Collections',
      ],
      editableDeposit: {
        id: null,
        key: null,
        name: "",
        accessionNumber: "",
        accessionId: "",
        identifier: "",
        archivesSpaceArchivalObjectRecordId: "",
        lastSubmittedForIngest: "",
        collectingArea: "Department of Archives and Modern Manuscripts",
      },
      isAdmin: false,
      originalSnapshot: null,
      rowRef: null,
      editFormValid: true,
      saving: false,

      // simple rules
      nameRules: [
        v => (v === null || v === undefined || v === '' || v.length <= 250) || 'Name must be <= 250 chars'
      ],
    };
  },
  computed: {
    // allow overriding save state in cases where only externalIdentifier is present
    canSave() {
    // form must be valid AND (changes OR explicit allowSaving)
    return this.isAdmin && this.editFormValid && (this.isDirty || this.allowSaving) && !this.saving;
    },
    // disable Save unless something actually changed
    isDirty() {
      if (!this.originalSnapshot) return false;
      return (
        (this.editableDeposit.name ?? '') !== (this.originalSnapshot.name ?? '') ||
        (this.editableDeposit.identifier ?? '') !== (this.originalSnapshot.identifier ?? '') ||
        (this.editableDeposit.accessionNumber ?? '') !== (this.originalSnapshot.accessionNumber ?? '') ||
        (this.editableDeposit.accessionId ?? '') !== (this.originalSnapshot.accessionId ?? '') ||
        (this.editableDeposit.archivesSpaceArchivalObjectRecordId ?? '') !== (this.originalSnapshot.archivesSpaceArchivalObjectRecordId ?? '') ||
        (this.editableDeposit.collectingArea ?? '') !== (this.originalSnapshot.collectingArea ?? '') ||
        (this.lastSubmittedForIngest ?? '') !== (this.originalSnapshot.lastSubmittedForIngest ?? '')
      );
    },

    /* === XOR rules (exactly one) ===
       Pair 1: accessionNumber XOR identifier
       Pair 2: accessionId (numeric when present) XOR archivesSpaceArchivalObjectRecordId
    */
    identifierRules() {
      return [
        v => (v === null || v === undefined || v === '' || String(v).length <= 200) || 'Identifier must be <= 200 chars',
        () => this.accessionVsIdentifierRule() || true,
      ];
    },
    externalIdRules() {
      return [
        v => (v === null || v === undefined || v === '' || String(v).length <= 200) || 'Accession Number must be <= 200 chars',
        () => this.accessionVsIdentifierRule() || true,
      ];
    },
    accessionRules() {
      return [
        v => (v === null || v === undefined || v === '' || /^\d+$/.test(String(v))) || 'Accession ID must be numeric',
        () => this.aspaceAccessionVsAOidRule() || true,
      ];
    },
    aaoidRules() {
      return [
        v => (v === null || v === undefined || v === '' || String(v).length <= 200) || 'Archival Object ID must be <= 200 chars',
        () => this.aspaceAccessionVsAOidRule() || true,
      ];
    },
    collectingAreaRules() {
      return [
        v => (v === '' || v === null || v === undefined || this.collectingAreaOptions.includes(v)) || 'Must be one of the listed options',
      ];
    },
  },
  watch: {
    'editableDeposit.accessionNumber'() { this.$refs.editForm?.validate?.(); },
    'editableDeposit.identifier'() { this.$refs.editForm?.validate?.(); },
    'editableDeposit.accessionId'() { this.$refs.editForm?.validate?.(); },
    'editableDeposit.archivesSpaceArchivalObjectRecordId'() { this.$refs.editForm?.validate?.(); },
  },
  async mounted() {
    try {
      if (this.$cognitoAuth?.getIdToken) {
        this.isAdmin = await ensureAdmin(this.$cognitoAuth, '105877');
      } else {
        const session = await Auth.currentSession();
        const token = session?.getIdToken?.()?.getJwtToken?.();
        this.isAdmin = !!token && isAdminFromToken(token, '105877');
      }
    } catch {
      this.isAdmin = false;
    }
  },
  methods: {
    hasExternalOnly(deposit) {
      const acc = (deposit?.accessionNumber ?? '').trim();
      const ext = (deposit?.externalIdentifier ?? '').trim();
      return !acc && !!ext;
    },
    isCloseBlocked(deposit) {
      return deposit?.status === 'open' && this.hasExternalOnly(deposit);
    },
    
    askClose(deposit) {
      if (!this.isAdmin) return;
      if (this.isCloseBlocked(deposit)) return;
      this.confirmTarget = deposit;
      this.confirmCloseOpen = true;
    },

    cancelClose() {
      this.confirmBusy = false;
      this.confirmCloseOpen = false;
      this.confirmResubmitOpen = false;
      this.confirmTarget = null;
    },

    async confirmClose() {
      if (!this.confirmTarget || this.isCloseBlocked(this.confirmTarget)) return;
      this.confirmBusy = true;
      try {
        await this.toggleDeposit(this.confirmTarget.id); // sets status=closed and stamps lastSubmittedForIngest
        this.cancelClose();
      } catch (e) {
        console.warn('Failed to close deposit', e);
        this.confirmBusy = false;
      }
    },

    askResubmit(deposit) {
      if (!this.isAdmin) return;
      this.confirmTarget = deposit;
      this.confirmResubmitOpen = true;
    },

    async confirmResubmit() {
      if (!this.confirmTarget) return;
      this.confirmBusy = true;
      try {
        await this.resubmitDeposit(this.confirmTarget.id);
        this.cancelClose(); // closes dialog + clears target
      } catch (e) {
        console.warn('Failed to resubmit deposit', e);
        this.confirmBusy = false; // keep dialog open so user can retry/cancel
      }
    },

    async resubmitDeposit(metadataId) {
      if (!this.isAdmin) return;
      const row = this.deposits.find(d => d.id === metadataId);
      if (!row) return;

      // copy for S3 write
      const metadata = { ...row };
      const nowIso = new Date().toISOString();

      // status remains CLOSED; just update lastSubmittedForIngest
      metadata.status = 'closed';
      metadata.lastSubmittedForIngest = nowIso;

      const depositKey = metadata.key;
      delete metadata.key;

      // optimistic UI update with revert on failure
      const prev = { lastSubmittedForIngest: row.lastSubmittedForIngest };
      row.lastSubmittedForIngest = nowIso;

      const ok = await this.writeMetadata(depositKey, metadata);
      if (!ok) {
        row.lastSubmittedForIngest = prev.lastSubmittedForIngest ?? null;
        throw new Error('S3 write failed');
      }
    },

    coalesceAccessionNumber(a, e) {
      const A = (a ?? '').trim();
      const E = (e ?? '').trim();

      if (A == '' && E != '') {
        this.allowSaving = true;
      }

      return A || E || '';
    },
    formatDate,

    getActionLabel(deposit) {
      return deposit?.lastSubmittedForIngest ? 'Resubmit' : 'Close & Submit';
    },
    getActionIcon(deposit) {
      // keep your archive icon, or pick a “resubmit” style icon for resubmits
      return deposit?.lastSubmittedForIngest ? 'mdi-reload' : 'mdi-archive';
    },

    async writeMetadata(objectKey, metadata) {
      try {
        const credentials = await Auth.currentCredentials();
        const client = new S3Client({
          region: config.region,
          credentials: Auth.essentialCredentials(credentials),
        });

        const params = {
          Bucket: config.bucketName,
          Key: objectKey,
          Body: JSON.stringify(metadata),
          CacheControl: "no-store, max-age=0, must-revalidate",
          ContentType: "application/json",
        };

        await client.send(new PutObjectCommand(params));
        return true;
      } catch (err) {
        console.log("Error:", err);
        return false;
      }
    },

    // ---- XOR helpers ----
    isBlank(v) {
      return v === null || v === undefined || String(v).trim() === '';
    },
    xorMessage(aVal, bVal, aLabel, bLabel) {
      const aBlank = this.isBlank(aVal);
      const bBlank = this.isBlank(bVal);
      if (aBlank && bBlank) return `Provide ${aLabel} OR ${bLabel}.`;
      if (!aBlank && !bBlank) return `Provide only one of ${aLabel} OR ${bLabel}, not both.`;
      return ''; // exactly one provided -> ok
    },
    accessionVsIdentifierRule() {
      return this.xorMessage(
        this.editableDeposit.accessionNumber,
        this.editableDeposit.identifier,
        'Accession Number',
        'Identifier'
      );
    },
    aspaceAccessionVsAOidRule() {
      return this.xorMessage(
        this.editableDeposit.accessionId,
        this.editableDeposit.archivesSpaceArchivalObjectRecordId,
        'AS Accession ID',
        'AS Archival Object ID'
      );
    },

    isSectionHeader(idx) {
      if (!this.groupByStatus) return false;
      const curr = this.deposits[idx]?.status ?? '';
      const prev = this.deposits[idx - 1]?.status ?? '';
      // Show a section header at the first row OR whenever status changes.
      return idx === 0 || curr !== prev;
    },

    sectionTitleFor(status) {
      return status === 'open' ? 'Open deposits' : 'Closed deposits';
    },

    toggleDeposit(metadataId) {
      if (!this.isAdmin) return;
      const depositMetadata = this.deposits.find((d) => d.id === metadataId);
      if (!depositMetadata) return;

      // make a copy we can serialize back to S3
      const metadata = { ...depositMetadata };
      const currStatus = metadata.status;

      // Generate date string for saving lastSubmittedForIngest on first submit
      const nowIso = new Date().toISOString();

      switch (currStatus) {
        case "closed":
          // If it’s "locked" (already submitted), do NOT allow reopen
          if (metadata.lastSubmittedForIngest) {
            console.warn('Deposit is locked (already submitted); cannot reopen.');
            return;
          }
          depositMetadata.status = metadata.status = "open";
          break;

        case "open":
          // Closing always sets lastSubmittedForIngest (first submission)
          depositMetadata.status = metadata.status = "closed";
          depositMetadata.lastSubmittedForIngest = metadata.lastSubmittedForIngest = nowIso;
          break;

        default:
          console.log(`Unknown status ${currStatus} in deposit`);
      }

      const depositKey = metadata.key;
      delete metadata.key;

      this.writeMetadata(depositKey, metadata);
    },

    openEdit(deposit) {
      if (!this.isAdmin) return;
      this.allowSaving = false;
      this.rowRef = deposit;
      this.editableDeposit = {
        id: deposit.id,
        key: deposit.key,
        name: deposit.name ?? "",
        identifier: deposit.identifier ?? "",
        accessionNumber: this.coalesceAccessionNumber(deposit.accessionNumber, deposit.externalIdentifier),
        archivesSpaceArchivalObjectRecordId: deposit.archivesSpaceArchivalObjectRecordId ?? "",
        accessionId: deposit.accessionId ?? "",
        collectingArea: deposit.collectingArea ?? "Department of Archives and Modern Manuscripts",
      };
      this.originalSnapshot = { 
          ...this.editableDeposit,
          collectingArea: deposit.collectingArea ?? "",   // raw value, not the display default — lets isDirty catch a first-time set
      };
      this.$nextTick(() => this.$refs.editForm?.resetValidation?.());
      this.editDialog = true;
    },

    // ✨ reset state when dialog closes
    onEditDialogToggle(val) {
      if (!val) {
        this.allowSaving = false;
        this.rowRef = null;
        this.originalSnapshot = null;
        this.editableDeposit = {
          id: null,
          key: null,
          name: "",
          accessionNumber: "",
          accessionId: "",
          identifier: "",
          archivesSpaceArchivalObjectRecordId: "",
          collectingArea: "Department of Archives and Modern Manuscripts",
        };
        this.$refs.editForm?.resetValidation?.();
      }
    },

    // ✨ validate, persist to S3, update the row optimistically
    async saveEdit() {
      if (!this.isAdmin) return;
      // run Vuetify validators
      if (!this.$refs.editForm?.validate?.()) return;

      // extra XOR safety
      const err1 = this.xorMessage(
        this.editableDeposit.accessionNumber,
        this.editableDeposit.identifier,
        'Accession Number',
        'Identifier'
      );
      const err2 = this.xorMessage(
        this.editableDeposit.accessionId,
        this.editableDeposit.archivesSpaceArchivalObjectRecordId,
        'AS Accession ID',
        'AS Archival Object ID'
      );
      if (err1 || err2) {
        this.$refs.editForm?.validate?.();
        return;
      }

      if (!this.rowRef) return;

      const metadata = { ...this.rowRef };

      // On save, trust ONLY what is in the form.
      // If the field is empty, we treat it as “no accession number”.
      const preferredAccession = (this.editableDeposit.accessionNumber ?? '').trim();

      metadata.name = this.editableDeposit.name ?? "";
      metadata.accessionNumber = preferredAccession;
      metadata.identifier = this.editableDeposit.identifier ?? "";
      metadata.archivesSpaceArchivalObjectRecordId = this.editableDeposit.archivesSpaceArchivalObjectRecordId ?? "";
      metadata.accessionId = this.editableDeposit.accessionId ?? "";
      metadata.collectingArea = this.editableDeposit.collectingArea ?? "";

      // We are wiping out the externalIdentifier at this point
      delete metadata.externalIdentifier;

      const depositKey = metadata.key;
      delete metadata.key;

      this.saving = true;
      const ok = await this.writeMetadata(depositKey, metadata);
      this.allowSaving = false;  // Ensure any override from coalesceAccessionNumber is reset.
      this.saving = false;

      if (ok) {
        // optimistic UI update
        this.rowRef.name = metadata.name;
        this.rowRef.accessionNumber = metadata.accessionNumber;
        this.rowRef.identifier = metadata.identifier;
        this.rowRef.archivesSpaceArchivalObjectRecordId = metadata.archivesSpaceArchivalObjectRecordId;
        this.rowRef.accessionId = metadata.accessionId;
        this.rowRef.collectingArea = metadata.collectingArea;

        // keep UI in sync: drop externalIdentifier from the row too
        delete this.rowRef.externalIdentifier;

        this.editDialog = false;
        this.$emit('refresh'); // If you want to re-fetch from S3 instead
      } else {
        console.warn("Failed to save updated metadata to S3");
      }
    },
  },
};
</script>

<style scoped>
/* ====== Table cell layout (original) ====== */
.truncate {
  min-width: 11%;
  max-width: 11%;
  justify-content: center;
}

/* lock action button widths so columns stay aligned */
:deep(td .v-btn) {
  min-width: 7rem;   /* or 10ch / 120px */
  max-width: 7rem;
  justify-content: center;
}

/* tighter icon-only buttons (e.g. Edit/View) */
:deep(td .v-btn.v-btn--icon) {
  min-width: 2.5rem;
  max-width: 2.5rem;
  justify-content: center;
}

/* Center the icon button horizontally in the View column */
:deep(td.view-cell) {
  text-align: center;
}

/* Normalize the icon button size and centering */
:deep(td.view-cell .v-btn.v-btn--icon) {
  width: 2.5rem;
  height: 2.5rem;
  margin: 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

/* Ensure the table fills the parent width */
:deep(.v-table__wrapper > table) {
  width: 100%;
  table-layout: fixed;
}

/* Allow content to wrap instead of forcing horizontal scroll */
:deep(.v-table__wrapper th),
:deep(.v-table__wrapper td) {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* If your container sits inside a dialog/card with padding you don't want */
:deep(.v-card-text),
:deep(.v-card) {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

/* ====== Form width/centering in dialog (original) ====== */
v-dialog input {
  width: 80%;
}

.form-narrow :deep(.v-input) {
  width: 80%;
  margin-left: auto;
  margin-right: auto;
}

/* On small screens, let them be full width */
@media (max-width: 600px) {
  .form-narrow :deep(.v-input) {
    width: 100%;
  }
}

/* ====== New: External labels with info (“i”) tooltip ====== */
.label-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;   /* center the label + info icon */
  gap: 0.4rem;
  margin: 0.25rem auto;
  width: 100%;
  text-align: center;        /* helps if label wraps to multiple lines */
}

.form-label {
  font-weight: 600;
  line-height: 1.2;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  /* no explicit width so it shrinks to content and centers nicely with the icon */
}

/* small grey info icon */
.info-icon :deep(.v-icon) {
  opacity: 0.7;
}
.info-icon:hover :deep(.v-icon),
.info-icon:focus-visible :deep(.v-icon) {
  opacity: 1;
}

/* ====== New: Pair layout + centered “OR” chip ====== */
.pair-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr; /* left | OR | right */
  gap: 0.75rem;
  align-items: start;
  width: 80%;
  margin: 0.75rem auto 0;
}

.w-80 {
  width: 80%;
  margin-left: auto;
  margin-right: auto;
}

.pair-left,
.pair-right {
  width: 100%;
}

/* “OR” pill */
.or-chip {
  align-self: center;
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1;
  padding: 0.4rem 0.6rem;
  border: 1px solid rgba(0,0,0,0.2);
  border-radius: 999px;
  user-select: none;
  opacity: 0.75;
}

/* Stack fields and relax widths on smaller screens */
@media (max-width: 800px) {
  .pair-row {
    grid-template-columns: 1fr;
    width: 100%;
  }
  .w-80 {
    width: 100%;
  }
  .or-chip {
    display: none; /* optional — show if you want it always visible */
  }
}

/* Wrap long tooltip text around ~100 characters */
:deep(.tip-wrap-100) {
  max-width: 100ch !important;          /* ≈ 100 characters */
  white-space: normal !important;        /* allow wrapping */
  overflow-wrap: anywhere;    /* break long words/URLs if needed */
  word-break: normal;
  text-align: left;           /* long text is easier to read left-aligned */
  line-height: 1.25;
}

/* Keep tooltips sensible on small screens */
@media (max-width: 600px) {
  :deep(.tip-wrap-100) { max-width: 90vw; }
}

.fallback-inline {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
  min-width: 0;
  max-width: 100%;
}

/* Make long IDs truncate nicely instead of wrapping or pushing the icon down */
.fallback-number {
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  word-break: keep-all !important;
  overflow-wrap: normal !important;
}

.fallback-red {
  color: #c62828;
  font-weight: 600;
}

.fallback-icon {
  flex: 0 0 auto;
  vertical-align: text-bottom;
  opacity: 0.9;
  margin-left: 0.1rem;
}
.fallback-icon:hover,
.fallback-icon:focus-visible {
  opacity: 1;
}

.open-text {
  color: green;
}
.closed-text {
  color: red;
}
:deep(td.long-button .v-btn) {
  height: auto;
  min-height: 2.5rem;
}

:deep(td.long-button .v-btn .v-btn__content) {
  white-space: normal !important;
  flex-wrap: nowrap;
  gap: 0.35rem;
  justify-content: center;
}

:deep(td.long-button .btn-text) {
  flex: 1 1 auto;
  min-width: 0;
  white-space: normal !important;
  overflow-wrap: anywhere;
  word-break: break-word;
  text-align: center;
}

/* Keep the icon fixed-size at the right */
:deep(td.long-button .v-btn .v-icon) {
  flex: 0 0 auto;
  align-self: center;
}

.confirmCard {
  text-align: center;
  padding: 5px;
}
</style>
